/**
 * Abstract class which provides most of the interface for navigating a directory structure based on `Document` models.
 * ## Fields
 * Name | Description
 * --- | ---
 * directories | The array of `DirectoryView` objects representing the current directory "path". The first item is the top-most directory, and the last item is the directory containing the currently-displayed set of `Document` models.
 * headings | An array of heading strings parallel to `directories`, representing the history of headings displayed since root.
 * @abstract
 * @module app/views/DirectoryContainerViewBase
 */
import Backbone from "backbone";
import _ from "underscore";
import $ from "jquery";
import jqAjaxProgress from "jq-ajax-progress";
import DocumentCollection from "app/models/DocumentCollection";
import Document from "app/models/Document";
import DirectoryEditView from "app/views/documents/DirectoryEditView";
import ModalView from "core/views/ModalView";
import FileEditView from "app/views/documents/FileEditView";
import DirectoryView from "app/views/DirectoryView";
import Field from "core/models/Field";
import UrlBuilder from "@HBKEngineering/url-builder";
import ErrorUtils from "app/utils/ErrorUtils";
import DragDropView from "app/views/documents/DragDropView";
import ImageUploadView from "app/views/documents/ImageUploadView";
import session from "app/Session";
import isMobile from "core/is-mobile";
import messenger from "app/Messenger";
import modernizr from "modernizr";

export default Backbone.View.extend({
  /**
   * Called during `initialize` if it is determined that drag-and-drop is not supported by the current environment.
   * @abstract
   */
  dragNotSupported: function () {},

  dropError: function () {
    messenger.warning(
      "Could not drop file. Try dragging the file from your desktop instead."
    );
  },

  /**
   * Called when the root directory is departed for a subdirectory.
   * @abstract
   */
  leftRoot: function () {},

  /**
   * Called when navigation returns to the root directory.
   * @abstract
   */
  atRoot: function () {},

  /**
   * Called when a new heading is to be displayed.
   * @param {string} heading The heading to set.
   * @abstract
   */
  setHeading: function (heading) {},

  /**
   * Called when a new directory is to be displayed.
   * @param {DirectoryView} directory The view to display.
   * @abstract
   */
  setView: function (directory) {},

  /**
   * Given a document model, derive the URL used to query child documents.
   * @param  {Document} folderModel The model in question.
   * @return {string}             The URL for the query.
   * @abstract
   */
  getFolderUrl: function (folderModel) {},

  /**
   * Given a document model, derive the folder name.
   * @param  {Document} folderModel The model in question.
   * @return {string}             The folder name.
   * @abstract
   */
  getFolderName: function (folderModel) {},

  /**
   * Given a document model, derive the URL to use to load or update the associated file.
   * @param  {Document} fileModel The document model.
   * @return {string}        The file URL.
   * @abstract
   */
  fileUrl: function (fileModel) {},

  /**
   * Initializes the control
   * @param  {object} options The options hash.
   * @param {string} options.title:'Files' The initial title for the container.
   * @param {Boolean} options.allowAddFolder:true True if the UI should allow addition of entire folders, otherwise false.
   * @param {Boolean} options.allowAddFiles:true True if the UI should allow addition of individual files, otherwise false. If both this and `allowAddFolder` are false, users will be prevented from modifying existing files.
   * @param {Boolean} options.allowDropFiles:true True to allow file drop (if supported by the browser), otherwise false.
   * @param {string} options.initialFolder The URL of the initial (`Document`) directory to display.
   */
  initialize: function (options) {
    this.options = _.defaults({}, options, {
      title: "Files",
      allowAddFolder: true,
      allowAddFiles: true,
      allowDropFiles: true,
    });
    this.directories = [];
    this.headings = [];

    if (!modernizr.draganddrop) {
      this.dragNotSupported();
    }

    this.createDropUI();

    this._render = new $.Deferred();
  },

  /**
   * Clears the navigation stack and displays the initial folder.
   */
  navigateStart: function () {
    _.each(this.directories, function (dir) {
      if (dir) {
        dir.remove();
      }
    });
    this.directories = [];
    this.headings = [];
    this.navigateTo(this.options.initialFolder, this.options.title);
  },

  /**
   * A function which shall be called by inheritors when rendering is complete.
   */
  doneRendering: function () {
    this._render.resolve();
  },

  createDropUI: function () {
    var self = this;

    self.dropZoneView = new DragDropView({
      appearance: DragDropView.DROP,
    });

    self.imageUploadView = new ImageUploadView({
      model: new Document({
        IsFolder: false,
        User: session.user.id,
      }),
    });

    self.imageUploadView.on("select-image", function (file, model) {
      _.last(self.directories).collection.add(model);
      self._postModel(model, file); //we're just going to assume we aren't using IE 9
      self.imageUploadView.setModel(
        new Document({
          IsFolder: false,
          User: session.user.id,
        })
      );
    });

    self.dropZoneView.on("drop", function (evt) {
      self.onDrop(evt);
    });

    self.dropZoneView.on("droperror", function () {
      self.dropError();
    });

    return self.dropZoneView;
  },

  /**
   * Displays the child `Document` models of the `Document` at the given URL, labeled by the given heading.
   * @param  {string} dirUrl  The URL of the `Document` model to display.
   * @param  {string} heading The heading to display.
   */
  navigateTo: function (dirUrl, heading) {
    var docCollection = new DocumentCollection(null, {
      url: dirUrl,
    });
    var self = this;

    docCollection.fetch({
      success: _.bind(function () {
        var directory = new DirectoryView({
          collection: docCollection,
          allowEdit: this.options.allowAddFiles || this.options.allowAddFolder,
          linkField: new Field({
            id: "progress",
            label: "",
            name: "progress",
            editor: "custom",
            ViewerView: {
              template: function (options) {
                var attrs = options.model.attributes;

                if (!attrs.id) {
                  if (attrs.IsFolder) {
                    return "New Folder";
                  } else {
                    if (attrs.Uri) {
                      return "Saving...";
                    } else {
                      return "Uploading...";
                    }
                  }
                } else {
                  if (attrs.IsFolder) {
                    return (
                      '<a href="#" data-doc-name="' +
                      attrs.Name +
                      '" data-doc-id="' +
                      attrs.id +
                      '" class="folder-select">View Folder</a>'
                    );
                  } else {
                    var linkText;
                    var progressWidth = 100;

                    if (attrs.Uri) {
                      //upload is complete
                      if (UrlBuilder.isUri(attrs.Uri)) {
                        linkText = "View";
                      } else {
                        linkText = "Download";
                      }

                      return (
                        '<a href="' +
                        self.fileUrl(options.model) +
                        '" target="_blank">' +
                        linkText +
                        "</a>"
                      );
                    } else {
                      if (_.isNumber(attrs.progress)) {
                        progressWidth = attrs.progress;
                      }

                      if (attrs.progress || attrs.progress === 0) {
                        return (
                          '<div class="progress progress-striped active" style="margin-top:7px; margin-bottom:0; width: 75px; height: 10px"><div class="progress-bar" style="width: ' +
                          progressWidth +
                          '%"></div></div>'
                        );
                      } else {
                        return '<span class="text-danger">Not Found</span>';
                      }
                    }
                  }
                }
              },

              setControlValue: function () {
                this.render();
              },
            },
            sortable: false,
            filterable: false,
            editable: false,
          }),
        });

        directory.on(
          "folder-select",
          function (folderModel) {
            this.navigateTo(
              this.getFolderUrl(folderModel),
              this.getFolderName(folderModel)
            );
          },
          this
        );
        var lastDirectory = _.last(this.directories);
        this.directories.push(directory);
        this.headings.push(heading);
        this._render.done(
          _.bind(function () {
            if (lastDirectory) {
              //i.e. we are not at the root directory
              lastDirectory.remove();
              this.leftRoot();
            } else {
              this.atRoot();
            }

            directory.render();
            this.setView(directory);
            this._updateDropZone();
            this.setHeading(heading);
          }, this)
        );
      }, this),
      error: ErrorUtils.apiHandler({
        activity: "loading the directory",
      }),
    });
  },

  /**
   * Navigate to the parent directory.
   */
  navigateUp: function () {
    var lastDirectory = this.directories.pop();

    this.headings.pop();
    var newDir = _.last(this.directories);
    var newHeading = _.last(this.headings);

    if (newDir) {
      lastDirectory.remove();
      lastDirectory.off("folder-select");
      this.setView(newDir);
      this._updateDropZone();
      this.setHeading(newHeading);
      newDir.delegateEvents();
    } else {
      this.directories.push(lastDirectory);
    }

    if (this.directories.length <= 1) {
      this.atRoot();
    }
  },

  _updateDropZone: function () {
    if (this.options.allowAddFiles && this.options.allowDropFiles) {
      var dropView = this.dropZoneView;

      if (isMobile) {
        this.imageUploadView.setModel(
          new Document({
            IsFolder: false,
            User: session.user.id,
          })
        );

        dropView = this.imageUploadView;
      }

      dropView.render();

      this.$el.append(dropView.$el);
    }
  },

  render: function () {
    this.doneRendering();
  },

  /**
   * Present a UI to the user for adding a folder to the current directory.
   */
  addFolderRequest: function () {
    var self = this;
    var buttons = [
      {
        text: "Add Folder",
        type: "primary",
      },
    ];
    var model = new Document({
      IsFolder: true,
      User: session.user.id,
    });
    var internalView = new DirectoryEditView({
      model: model,
    });
    var dialogView = new ModalView({
      title: "Add Folder",
      buttons: buttons,
    });

    dialogView.render();
    dialogView.buttons[0].once(
      "click",
      function () {
        internalView.commit();
        _.last(this.directories).collection.add(model);
        model.save(null, {
          error: ErrorUtils.apiHandler(),
          success: function () {
            //refresh the table so you can view the link to go to
            _.last(self.directories).table.refreshCurrentPage();
          },
        });
        dialogView.remove();
      },
      this
    );
    dialogView.$el.appendTo("body");
    dialogView.setContent(internalView.render().$el); //have to do this here otherwise IE throws an error
    dialogView.once(
      "hidden",
      function () {
        dialogView.buttons[0].off("click");
      },
      this
    );
    dialogView.show();
  },

  /**
   * Present a UI to the user for adding a file to the current directory.
   */
  addFileRequest: function () {
    var self = this;
    var buttons = [
      {
        text: "Upload File",
        type: "primary",
      },
    ];
    var model = new Document({
      IsFolder: false,
      User: session.user.id,
    });
    var internalView = new FileEditView({
      model: model,
    });
    var dialogView = new ModalView({
      title: "Upload File",
      buttons: buttons,
    });

    dialogView.render();
    dialogView.buttons[0].once(
      "click",
      function () {
        internalView.commit();
        _.last(this.directories).collection.add(model);

        if (internalView.isFileType()) {
          //capture the file and start uploading it.
          var files = internalView.getFileControl()[0].files;

          if (files) {
            var file = files[0];

            this._postModel(model, file);
          } else {
            this._postModelLegacy(model, internalView.$el);
          }
        } else {
          model.save(null, {
            error: ErrorUtils.apiHandler(),
            success: function () {
              //update the view to show a download link which should be model.URI
              _.last(self.directories).table.refreshCurrentPage();
            },
          });
        }

        dialogView.remove();
      },
      this
    );
    dialogView.$el.appendTo("body");
    dialogView.setContent(internalView.render().$el); //have to do this here otherwise IE throws an error
    dialogView.once(
      "hidden",
      function () {
        dialogView.buttons[0].off("click");
      },
      this
    );
    dialogView.show();
  },

  _postFileLegacy: function (fileModel, form) {
    var defer = new $.Deferred();

    $(form)
      .ajaxSubmit({
        url: this.fileUrl(fileModel),
        success: _.bind(defer.resolve, defer),
        error: _.bind(defer.reject, defer),
      })
      .fail(ErrorUtils.ajaxHandler());

    return defer.promise();
  },

  _postModelLegacy: function (model, form) {
    if (!!jqAjaxProgress) {
      console.log("JQ AJAX PROGRESS is not undefined"); //literally just so that the import doesn't randomly get removed
    }

    model.set("MimeType", "temp"); //I Hate IE9
    model.save(null, {
      success: _.bind(function (model) {
        model.set("progress", true);
        this._postFileLegacy(model, form)
          .done(function () {
            model.fetch().always(function () {
              model.set("progress", false);
            });
          })
          .uploadProgress(function (progressEvent) {
            if (progressEvent.lengthComputable) {
              model.set(
                "progress",
                (progressEvent.loaded / progressEvent.total) * 100
              );
            } else {
              model.set("progress", true);
            }
          });
      }, this),
      error: ErrorUtils.apiHandler(),
    });
  },

  _postFile: function (fileModel, fileObj, filename) {
    var data = new FormData();

    data.append("file", fileObj, filename);

    return this._ajaxFile(this.fileUrl(fileModel), data, "POST");
  },

  _ajaxFile: function (url, file, type) {
    var contentType = file.type ? file.type : false;

    return $.ajax(url, {
      data: file,
      processData: false,
      type: type,
      contentType: contentType,
      cache: false,
    }).fail(ErrorUtils.ajaxHandler());
  },

  _postModel: function (model, file) {
    var self = this;
    model.set("MimeType", file.type);
    model.set("FileName", file.name);
    model.save(null, {
      success: _.bind(function (model) {
        model.set("progress", true);

        function resetDropZone() {
          self.dropZoneView.setAppearance(DragDropView.DONE);
          self.imageUploadView.setAppearance(DragDropView.DONE);
          setTimeout(function () {
            self.dropZoneView.setAppearance(DragDropView.DROP);
            self.imageUploadView.setAppearance(DragDropView.DROP);
          }, 1000);
        }

        self.dropZoneView.setAppearance(DragDropView.PROGRESS);
        self.imageUploadView.setAppearance(DragDropView.PROGRESS);

        var uploadInfo = model.get("uploadInfo");

        if (uploadInfo && uploadInfo.presignedUrl) {
          this._ajaxFile(uploadInfo.presignedUrl, file, "PUT")
            .uploadProgress(function (progressEvent) {
              if (progressEvent.lengthComputable) {
                model.set(
                  "progress",
                  (progressEvent.loaded / progressEvent.total) * 100
                );
              } else {
                model.set("progress", true);
              }
            })
            .done(function () {
              var uri = uploadInfo.uri;

              model.unset("uploadInfo");
              model.save(
                { Uri: uri },
                {
                  success: function (model) {
                    model.set("progress", false);
                    resetDropZone();
                  },
                  error: ErrorUtils.apiHandler(self.options.context),
                }
              );
            });
        } else {
          this._postFile(model, file, file.name)
            .uploadProgress(function (progressEvent) {
              if (progressEvent.lengthComputable) {
                model.set(
                  "progress",
                  (progressEvent.loaded / progressEvent.total) * 100
                );
              } else {
                model.set("progress", true);
              }
            })
            .done(function () {
              model.fetch().always(function () {
                model.set("progress", false);
              });
              resetDropZone();
            });
        }
      }, this),
      error: ErrorUtils.apiHandler(),
    });
  },

  onDrop: function (evt) {
    evt.preventDefault();

    messenger.reset();

    if (this.options.allowDropFiles) {
      _.each(
        evt.originalEvent.dataTransfer.files,
        _.bind(function (file) {
          var model = new Document({
            IsFolder: false,
            Name: file.name,
            MimeType: file.type,
            User: session.user.id,
          });

          _.last(this.directories).collection.add(model);
          this._postModel(model, file);
        }, this)
      );
    }
  },
});



//////////////////
// WEBPACK FOOTER
// ./public/js/app/views/DirectoryContainerViewBase.js
// module id = 494
// module chunks = 1